/** Preferences › Driver declarations — per-tenant yes/no statements on the booking form Driver tab. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, ClipboardCheck, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listDriverDeclarations, saveDriverDeclaration, deleteDriverDeclaration, listSitesLite } from "@/lib/api";
import DeclarationNoticeEditor from "@/components/DeclarationNoticeEditor";

const BOTH = "__both__";

function useTenantId() {
  const { assignments = [], hasRole } = useAuth();
  const { viewAsClientId } = useViewAsClient();
  return hasRole(["super_admin"]) ? viewAsClientId : assignments.find((a) => a.client_id)?.client_id || "";
}

const SitePicker = ({ sites, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    <button type="button" onClick={() => onChange(null)}
            className={`text-xs px-2 py-0.5 rounded-full border ${!value?.length ? "bg-slate-900 text-white" : "bg-white"}`}>
      All sites
    </button>
    {sites.map((s) => {
      const on = (value || []).includes(s.id);
      return (
        <button key={s.id} type="button"
                onClick={() => onChange(on ? value.filter((x) => x !== s.id) : [...(value || []), s.id])}
                className={`text-xs px-2 py-0.5 rounded-full border ${on ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>
          {s.name}
        </button>
      );
    })}
  </div>
);

const DeclarationRow = ({ d, sites, onSave, onDelete, onMove, isFirst, isLast }) => {
  const [v, setV] = useState(d);
  useEffect(() => setV(d), [d]);
  const dirty = JSON.stringify(v) !== JSON.stringify(d);
  const set = (k) => (val) => setV((x) => ({ ...x, [k]: val }));
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${dirty ? "border-amber-300 bg-amber-50/40" : "border-slate-200"} ${v.is_active ? "" : "opacity-60"}`}
         data-testid={`decl-row-${d.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
        <Input className="md:col-span-6" value={v.statement} onChange={(e) => set("statement")(e.target.value)}
               placeholder="Statement the driver ticks" data-testid={`decl-statement-${d.id}`} />
        <Input className="md:col-span-2" value={v.short_label} onChange={(e) => set("short_label")(e.target.value)}
               placeholder="Short label" data-testid={`decl-label-${d.id}`} />
        <Select value={v.direction || BOTH} onValueChange={(x) => set("direction")(x === BOTH ? null : x)}>
          <SelectTrigger className="md:col-span-2" data-testid={`decl-direction-${d.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={BOTH}>In &amp; out</SelectItem>
            <SelectItem value="inbound">Inbound only</SelectItem>
            <SelectItem value="outbound">Outbound only</SelectItem>
          </SelectContent>
        </Select>
        <div className="md:col-span-2 flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={isFirst} onClick={() => onMove(-1)} data-testid={`decl-up-${d.id}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={isLast} onClick={() => onMove(1)} data-testid={`decl-down-${d.id}`}><ArrowDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={onDelete} data-testid={`decl-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-2"><Switch checked={v.is_active} onCheckedChange={set("is_active")} data-testid={`decl-active-${d.id}`} /> Shown on form</label>
        <label className="inline-flex items-center gap-2"><Switch checked={v.is_required} onCheckedChange={set("is_required")} data-testid={`decl-required-${d.id}`} /> Mandatory for site access</label>
        <SitePicker sites={sites} value={v.site_ids} onChange={set("site_ids")} testId={`decl-sites-${d.id}`} />
        {dirty && <Button size="sm" className="ml-auto" onClick={() => onSave(v)} data-testid={`decl-save-${d.id}`}>Save</Button>}
      </div>
    </div>
  );
};

export default function AdminDriverDeclarations() {
  const clientId = useTenantId();
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);

  const load = useCallback(async () => {
    if (!clientId) { setRows([]); return; }
    const [{ data, error }, { data: s }] = await Promise.all([listDriverDeclarations(clientId), listSitesLite(clientId)]);
    if (error) toast.error(error.message);
    setRows(data || []);
    setSites(s || []);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const save = async (row, msg = "Saved") => {
    if ("statement" in row && (row.statement || "").trim().length < 3) { toast.error("Statement needs at least 3 characters"); return; }
    if ("short_label" in row && !(row.short_label || "").trim()) { toast.error("Short label can't be empty"); return; }
    const { error } = await saveDriverDeclaration({ ...row, client_id: clientId });
    if (error) { toast.error(error.message); return; }
    toast.success(msg);
    load();
  };
  const add = () => save({ statement: "New declaration", short_label: "New", is_active: false, is_required: false,
                            sort_order: (rows.at(-1)?.sort_order || 0) + 10 }, "Added — edit the wording, then switch it on");
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.statement}"? Past bookings keep their answers.`)) return;
    const { error } = await deleteDriverDeclaration(d.id);
    if (error) toast.error(error.message); else load();
  };
  const move = async (i, dir) => {
    const a = rows[i], b = rows[i + dir];
    await Promise.all([saveDriverDeclaration({ id: a.id, sort_order: b.sort_order }),
                       saveDriverDeclaration({ id: b.id, sort_order: a.sort_order })]);
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-driver-declarations">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6 text-slate-700" /> Driver declarations
        </h1>
        <p className="text-sm text-slate-500">Yes/no statements on the booking form&apos;s Driver tab and public booking page.
          Answers are saved with the wording at booking time and shown in the booking emails.</p>
      </div>
      {!clientId ? (
        <Card><CardContent className="p-6 text-sm text-slate-500" data-testid="decl-pick-tenant">Pick a tenant in the top-right picker.</CardContent></Card>
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{rows.length} declaration{rows.length === 1 ? "" : "s"}</CardTitle>
            <Button size="sm" variant="outline" onClick={add} data-testid="decl-add-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {rows.map((d, i) => (
              <DeclarationRow key={d.id} d={d} sites={sites} onSave={(v) => save(v)} onDelete={() => remove(d)}
                              onMove={(dir) => move(i, dir)} isFirst={i === 0} isLast={i === rows.length - 1} />
            ))}
          </CardContent>
        </Card>
      )}
      {clientId && <DeclarationNoticeEditor clientId={clientId} sites={sites} />}
    </div>
  );
}
